export const CLIENT_ENTRA_SETUP_SCRIPT_ITEM_NAME = 'ClientEntraSetupScript';

export const CLIENT_ENTRA_SETUP_SCRIPT_OPERATION =
  'GenerateClientEntraSetupScript';

export const isMicrosoftEntraAuthentication = (value) => (
  String(value || '').trim().toLowerCase() === 'microsoft entra id'
);

const safeFilePart = (value, fallback) => {
  const safe = String(value || '')
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return safe || fallback;
};

const formatEmailDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString();
};

export const buildClientEntraSetupEmail = ({
  run,
  currentEnvironment,
}) => {
  const clientName = String(
    run?.Name
    || run?.ClientName
    || run?.DisplayName
    || run?.ShortName
    || 'the client',
  ).trim();
  const environment = String(
    run?.DisplayEnvironment
    || run?.CurrentEnvironment
    || currentEnvironment
    || '',
  ).trim().toUpperCase();
  const tenantId = String(run?.EntraTenantId || '').trim();
  const tenantDomain = String(run?.EntraTenantDomain || '').trim();
  const fileName = [
    'Convoke-Entra-Setup',
    safeFilePart(run?.ShortName, 'Client'),
    safeFilePart(environment, 'Environment'),
  ].join('-') + '.ps1';
  const expiresAt = formatEmailDate(
    run?.EntraSetupTokenExpiresAtUtc,
  );
  const tenantDescription = tenantId
    ? `${tenantId}${tenantDomain ? ` (${tenantDomain})` : ''}`
    : 'The script will prompt for the exact Microsoft Entra tenant ID.';
  const expirationInstruction = expiresAt
    ? `The attached script's one-time Convoke authorization expires ${expiresAt}.`
    : 'The attached script contains a short-lived, one-time Convoke authorization.';
  const localInstruction = environment === 'LOCAL'
    ? [
      '',
      'LOCAL testing note:',
      '- This LOCAL script calls a loopback Convoke API. Run it on the Convoke developer workstation where the local Web API is running.',
    ].join('\n')
    : '';
  const subject = `Convoke Microsoft Entra setup for ${clientName}`
    + (environment ? ` (${environment})` : '');
  const body = [
    'Hello,',
    '',
    `We are preparing ${clientName} to use Microsoft Entra ID with Convoke${environment ? ` in ${environment}` : ''}. Please have a Microsoft Entra tenant administrator complete the attached ${fileName} script.`,
    '',
    'Before you begin:',
    `- Intended tenant: ${tenantDescription}`,
    '- Use an administrator account that can manage application registrations and grant tenant-wide admin consent.',
    '- The setup session requests Application.ReadWrite.All, AppRoleAssignment.ReadWrite.All, and Organization.Read.All. These permissions belong only to the temporary Microsoft Graph PowerShell setup session.',
    `- ${expirationInstruction} If it expires, stop and request a newly generated script.`,
    '- Treat the attached script as sensitive and send it only to the intended tenant administrator.',
    '',
    'Run a read-only plan first:',
    `1. Save ${fileName} in your Downloads folder. If the browser renamed it, use the downloaded name in the commands below.`,
    '2. Open Windows PowerShell.',
    '3. Change to the Downloads folder:',
    '   cd "$env:USERPROFILE\\Downloads"',
    '4. Review the proposed changes without modifying the tenant:',
    `   powershell.exe -NoLogo -NoProfile -ExecutionPolicy Bypass -File ".\\${fileName}" -PlanOnly -UseDeviceCode`,
    '5. Follow the device-code sign-in instructions and confirm that the script connected to the intended tenant.',
    '',
    'Complete the setup:',
    `1. Run: powershell.exe -NoLogo -NoProfile -ExecutionPolicy Bypass -File ".\\${fileName}" -UseDeviceCode`,
    '2. Review the exact proposed changes.',
    '3. Type CONFIGURE only when the displayed tenant and changes are correct.',
    '4. Wait for "Microsoft Entra setup completed successfully."',
    '',
    'What the script configures:',
    '- A public, single-tenant browser application using Authorization Code + PKCE. It receives no client secret.',
    '- A separate protected application with Microsoft Graph User.Read.All and GroupMember.Read.All application permissions.',
    '- Tenant admin consent and Convoke sign-in preauthorization.',
    '- A protected credential that is sent directly to Convoke over the one-time authenticated connection, encrypted, and validated.',
    '- Convoke validates the protected server-side connection without requiring a client user account or interactive login.',
    '',
    'What the script does not do:',
    '- It grants no Microsoft Graph write permission to either Convoke application.',
    '- It creates or changes no users, groups, roles, Convoke client records, Azure subscriptions, resource groups, networks, or Azure resources.',
    '- It does not display the protected secret or write it to a result file.',
    '',
    'When successful, reply only to confirm completion. Do not email credentials, screenshots containing codes, or secret values.',
    localInstruction,
    '',
    'Thank you.',
  ].filter((line) => line !== null && line !== undefined).join('\n');

  return {
    subject,
    body,
    clipboardText: `Subject: ${subject}\n\n${body}`,
    fileName,
  };
};

export const downloadClientEntraSetupScript = (scriptItem) => {
  const content = String(scriptItem?.ContentBase64 || '');
  const fileName = String(scriptItem?.FileName || '');
  if (!content || !/^[A-Za-z0-9._-]+\.ps1$/i.test(fileName)) {
    throw new Error(
      'The generated Microsoft Entra setup script is incomplete.',
    );
  }

  const binary = globalThis.atob(content);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const blob = new Blob(
    [bytes],
    { type: scriptItem?.MimeType || 'text/plain;charset=utf-8' },
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  try {
    link.href = url;
    link.download = fileName;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    URL.revokeObjectURL(url);
  }
};
